/**
 * API client for scoring-api service.
 * All /api/* calls go through Nginx → scoring-api:3200.
 * Auth /auth/* calls go through Nginx → scoring-api:3200 /auth/ routes.
 *
 * Token injection: access token is stored in AuthContext and injected
 * via getAccessToken() — a function callers provide. This avoids a
 * circular dep between api.ts and AuthContext.
 */

const BASE_URL = '/api';

// ── Access token provider ─────────────────────────────────────
// Set by AuthProvider on mount. api.ts reads this to inject Bearer tokens.
let _getAccessToken: (() => string | null) | null = null;
let _refresh: (() => Promise<string | null>) | null = null;

export function configureApiAuth(
  getToken: () => string | null,
  refresh: () => Promise<string | null>,
): void {
  _getAccessToken = getToken;
  _refresh = refresh;
}

// ── Core request helper ───────────────────────────────────────

async function request<T>(path: string, options?: RequestInit, retrying = false): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  const token = _getAccessToken?.();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  // On 401 — attempt one silent refresh and retry
  if (res.status === 401 && !retrying && _refresh) {
    const newToken = await _refresh();
    if (newToken) {
      return request<T>(path, options, true);
    }
  }

  const body = await res.json();
  if (!res.ok) {
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return body as T;
}

// ============================================================
// Game API
// ============================================================

export interface CreateGamePayload {
  brandId?: string;
  gameType: 'standard21' | 'skins';
  scoringMode: 'simple' | 'intermediate' | 'advanced';
  team1: { name: string; players: Array<{ id: string; name: string }> };
  team2: { name: string; players: Array<{ id: string; name: string }> };
  tournamentId?: string;
  courtId?: string;
}

export async function createGame(payload: CreateGamePayload) {
  return request<{ game: import('@aco/shared').Game }>('/games', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getGame(gameId: string) {
  return request<{ game: import('@aco/shared').Game }>(`/games/${gameId}`);
}

export async function submitRound(
  gameId: string,
  payload: {
    team1Score: number;
    team2Score: number;
    playerStats?: Array<{
      playerId: string;
      bagsIn: number;
      bagsOn: number;
      bagsOff: number;
      totalPoints: number;
    }>;
    source: string;
  },
) {
  return request<{ game: import('@aco/shared').Game }>(`/games/${gameId}/rounds`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function patchRound(
  gameId: string,
  roundNumber: number,
  payload: {
    team1Score: number;
    team2Score: number;
    playerStats?: Array<{
      playerId: string;
      bagsIn: number;
      bagsOn: number;
      bagsOff: number;
      totalPoints: number;
    }>;
    source: string;
  },
) {
  return request<{ game: import('@aco/shared').Game }>(
    `/games/${gameId}/rounds/${roundNumber}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
}

/** Alias matching the canonical name used in ScoreCorrectionFlow. */
export async function updateRound(
  gameId: string,
  roundNumber: number,
  payload: {
    team1Score: number;
    team2Score: number;
    playerStats?: any[];
    source: string;
  },
) {
  return request<{ game: import('@aco/shared').Game }>(
    `/games/${gameId}/rounds/${roundNumber}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
}

export async function patchScoringMode(
  gameId: string,
  scoringMode: 'intermediate' | 'advanced',
) {
  return request<{ game: import('@aco/shared').Game }>(
    `/games/${gameId}/scoring-mode`,
    { method: 'PATCH', body: JSON.stringify({ scoringMode }) },
  );
}

// ============================================================
// Player API
// ============================================================

export interface PlayerResult {
  id: string;
  name: string;
  email?: string;
}

export async function searchPlayers(q: string, limit = 10): Promise<{ players: PlayerResult[] }> {
  return request<{ players: PlayerResult[] }>(
    `/players/search?q=${encodeURIComponent(q)}&limit=${limit}`,
  );
}

export async function getRecentPlayers(limit = 10): Promise<{ players: PlayerResult[] }> {
  return request<{ players: PlayerResult[] }>(`/players/recent?limit=${limit}`);
}

export async function createPlayer(
  name: string,
  email?: string,
): Promise<{ player: PlayerResult }> {
  return request<{ player: PlayerResult }>('/players', {
    method: 'POST',
    body: JSON.stringify({ name, email }),
  });
}
